import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Gauge, TrendingUp, Wrench, SearchCheck, AlertTriangle, Target, MessageSquareText } from 'lucide-react';

const FRONTS = ['self', 'supervisor', 'internal_client'];


// TP-UAT (M): deterministic gap-pattern classifier (parity with backend engine).
export function classifyGapPattern(fronts) {
  const s = fronts?.self, sup = fronts?.supervisor, ic = fronts?.internal_client;
  const have = [s, sup, ic].filter((v) => v !== null && v !== undefined);
  if (have.length < 3) return 'pairwise_gap';
  const cands = {
    internal_higher: ic - Math.max(s, sup),
    internal_lower: Math.min(s, sup) - ic,
    self_perception_gap: s - Math.max(sup, ic),
    under_recognized_strength: Math.min(sup, ic) - s,
    supervisor_lower: Math.min(s, ic) - sup,
    supervisor_higher: sup - Math.max(s, ic),
  };
  const best = Object.keys(cands).reduce((a, b) => (cands[a] >= cands[b] ? a : b));
  return cands[best] >= 10 ? best : 'mixed_perception';
}

export function dimLabel(t, code) {
  return t(`talentperformance.dimensions.${code}`, {
    defaultValue: (code || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
  });
}

function ScoreBar({ value }) {
  if (value === null || value === undefined) return <span className="text-xs text-muted-foreground">—</span>;
  const color = value >= 80 ? 'bg-success' : value >= 60 ? 'bg-info' : value >= 40 ? 'bg-warning' : 'bg-destructive';
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="h-2 flex-1 rounded-full bg-muted overflow-hidden min-w-[60px]">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(100, value)}%` }} />
      </div>
      <span className="text-xs font-semibold w-9 text-right shrink-0">{value}</span>
    </div>
  );
}

function Section({ icon: Icon, title, items, empty, render, tone = 'text-primary' }) {
  return (
    <div className="space-y-1.5">
      <h4 className={`text-sm font-semibold flex items-center gap-2 ${tone}`}><Icon className="h-4 w-4" />{title}</h4>
      {(!items || items.length === 0)
        ? <p className="text-xs text-muted-foreground italic">{empty}</p>
        : items.map((it, i) => <div key={i} className="rounded border border-border/60 px-3 py-2 text-sm">{render(it)}</div>)}
    </div>
  );
}

export function PerfProfileView({ results, evaluation }) {
  const { t } = useTranslation();
  if (!results) return null;
  const fronts = results.fronts || {};
  const weights = results.weights || {};
  const cons = results.consolidated || {};
  const fLabel = (f) => t(`talentperformance.fronts.${f}`);
  const th = results.thresholds || {};
  return (
    <div className="space-y-5" data-testid="tp-profile-view">
      {/* fronts summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {FRONTS.map((f) => (
          <div key={f} className="rounded-lg border border-border/60 p-3 space-y-1" data-testid={`tp-front-card-${f}`}>
            <p className="text-xs text-muted-foreground">{fLabel(f)}{f === 'internal_client' && fronts[f]?.evaluator_count > 0 ? ` (${fronts[f].evaluator_count})` : ''}</p>
            <p className="text-2xl font-bold">{fronts[f]?.overall ?? '—'}</p>
            <p className="text-[11px] text-muted-foreground">{weights[f] > 0 ? `${weights[f]}%` : t('talentperformance.profile.notWeighted', { defaultValue: 'Not weighted (0%)' })}</p>
          </div>
        ))}
        <div className="rounded-lg border-2 border-primary/40 bg-primary/5 p-3 space-y-1" data-testid="tp-consolidated-card">
          <p className="text-xs text-primary font-medium">{t('talentperformance.profile.consolidated', { defaultValue: 'Weighted consolidated' })}</p>
          <p className="text-2xl font-bold text-primary">{cons.overall ?? '—'}</p>
          <p className="text-[11px] text-muted-foreground">100%</p>
        </div>
      </div>

      {/* dimensions comparison */}
      <div className="space-y-2">
        <h4 className="text-sm font-semibold flex items-center gap-2"><Gauge className="h-4 w-4" />{t('talentperformance.profile.byDimension', { defaultValue: 'Results by dimension' })}</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]" data-testid="tp-dimension-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-muted-foreground border-b">
                <th className="text-left py-1.5 pr-2 font-medium">{t('talentperformance.profile.dimension', { defaultValue: 'Dimension' })}</th>
                {FRONTS.map((f) => <th key={f} className="text-left py-1.5 px-2 font-medium">{fLabel(f)}</th>)}
                <th className="text-left py-1.5 pl-2 font-medium">{t('talentperformance.profile.consolidatedShort', { defaultValue: 'Consolidated' })}</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(cons.dimensions || {}).sort((a, b) => b[1] - a[1]).map(([dim, score]) => (
                <tr key={dim} className="border-b last:border-0">
                  <td className="py-2 pr-2 font-medium">{dimLabel(t, dim)}</td>
                  {FRONTS.map((f) => (
                    <td key={f} className="py-2 px-2 w-[130px]"><ScoreBar value={fronts[f]?.dimensions?.[dim] ?? null} /></td>
                  ))}
                  <td className="py-2 pl-2 w-[130px]"><ScoreBar value={score} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Separator />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Section icon={TrendingUp} tone="text-success" title={t('talentperformance.profile.strengths', { defaultValue: 'Strengths' })}
          items={results.strengths} empty={t('talentperformance.profile.none', { defaultValue: 'No items identified.' })}
          render={(s) => (
            <span><b>{dimLabel(t, s.dimension)}</b> — {s.score}
              <span className="block text-xs text-muted-foreground mt-0.5">
                {t('talentperformance.narratives.item_strength', { dim: dimLabel(t, s.dimension), score: s.score, defaultValue: '' })}
              </span>
            </span>
          )} />
        <Section icon={Wrench} tone="text-info" title={t('talentperformance.profile.opportunities', { defaultValue: 'Development opportunities' })}
          items={results.development_opportunities} empty={t('talentperformance.profile.none', { defaultValue: 'No items identified.' })}
          render={(s) => (
            <span><b>{dimLabel(t, s.dimension)}</b> — {s.score}
              <span className="block text-xs text-muted-foreground mt-0.5">
                {t('talentperformance.narratives.item_opportunity', { dim: dimLabel(t, s.dimension), score: s.score, defaultValue: '' })}
              </span>
            </span>
          )} />
        <Section icon={SearchCheck} tone="text-warning" title={t('talentperformance.profile.findings', { defaultValue: 'Relevant findings' })}
          items={results.relevant_findings} empty={t('talentperformance.profile.none', { defaultValue: 'No items identified.' })}
          render={(s) => (
            <span><b>{dimLabel(t, s.dimension)}</b> — {t('talentperformance.profile.gap', { defaultValue: 'Gap' })}: {s.gap}
              <span className="block text-xs text-muted-foreground mt-0.5">
                {t(`talentperformance.narratives.${classifyGapPattern(s.fronts)}`, { dim: dimLabel(t, s.dimension), gap: s.gap, defaultValue: '' })}
              </span>
            </span>
          )} />
        <Section icon={AlertTriangle} tone="text-destructive" title={t('talentperformance.profile.alerts', { defaultValue: 'Performance alerts' })}
          items={results.performance_alerts} empty={t('talentperformance.profile.none', { defaultValue: 'No items identified.' })}
          render={(s) => (
            <span><b>{dimLabel(t, s.dimension)}</b> — {s.score}
              <span className="block text-xs text-muted-foreground mt-0.5">
                {t('talentperformance.narratives.item_alert', { dim: dimLabel(t, s.dimension), score: s.score, defaultValue: '' })}
              </span>
            </span>
          )} />
      </div>

      <Section icon={Target} title={t('talentperformance.profile.priorities', { defaultValue: 'Development priorities' })}
        items={results.development_priorities} empty={t('talentperformance.profile.none', { defaultValue: 'No items identified.' })}
        render={(s) => <span><b>{dimLabel(t, s.dimension)}</b> — {s.score}</span>} />

      {FRONTS.some((f) => (results.front_comments?.[f] || []).length > 0) && (
        <div className="space-y-1.5">
          <h4 className="text-sm font-semibold flex items-center gap-2"><MessageSquareText className="h-4 w-4" />{t('talentperformance.profile.comments', { defaultValue: 'Qualitative comments' })}</h4>
          {FRONTS.map((f) => (results.front_comments?.[f] || []).map((c, i) => (
            <div key={`${f}-${i}`} className="rounded border border-border/60 px-3 py-2 text-sm">
              <Badge variant="outline" className="mb-1 text-[10px]">{fLabel(f)}</Badge>
              <p className="whitespace-pre-wrap text-muted-foreground">{c}</p>
            </div>
          )))}
        </div>
      )}

      <p className="text-[11px] text-muted-foreground" data-testid="tp-methodology-note">
        {t('talentperformance.profile.methodology', { defaultValue: '' , ...th })}
      </p>
      <p className="text-[11px] text-muted-foreground" data-testid="tp-thresholds-note">
        {t('talentperformance.profile.thresholdsNote', { defaultValue: 'Analytical thresholds (0–100): relevant gap ≥ {{gap}} · strength ≥ {{strength}} · opportunity ≤ {{development}} · alert ≤ {{alert}}. Results support the performance conversation; they are never automatic employment decisions.', gap: th.gap, strength: th.strength, development: th.development, alert: th.alert })}
      </p>
    </div>
  );
}
